import React from 'react';
import styled from 'styled-components';
import { FaGithub, FaTwitter, FaDiscord } from 'react-icons/fa';

const FooterSection = styled.footer`
  background-color: #080810;
  padding: 4rem 0 2rem;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, var(--primary), var(--secondary));
    opacity: 0.7;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
`;

const FooterColumn = styled.div``;

const FooterLogo = styled.div`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--secondary);
  margin-bottom: 1.5rem;

  span {
    color: var(--primary);
  }
`;

const FooterDescription = styled.p`
  color: var(--text-secondary);
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
`;

const SocialLink = styled.a`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-primary);
  transition: all 0.3s ease;
  font-size: 1.2rem;

  &:hover {
    background-color: var(--primary);
    color: white;
    transform: translateY(-3px);
  }
`;

const FooterTitle = styled.h3`
  font-size: 1.2rem;
  color: var(--secondary);
  margin-bottom: 1.5rem;
  position: relative;
  padding-bottom: 0.5rem;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 40px;
    height: 3px;
    background: linear-gradient(90deg, var(--primary), var(--secondary));
    border-radius: 1.5px;
  }
`;

const FooterLinks = styled.ul`
  list-style: none;
`;

const FooterLink = styled.li`
  margin-bottom: 0.8rem;
`;

const StyledLink = styled.a`
  color: var(--text-secondary);
  transition: color 0.3s ease;
  display: inline-flex;
  align-items: center;

  &:hover {
    color: var(--secondary);
  }

  &::before {
    content: '>';
    margin-right: 0.5rem;
    color: var(--primary);
    font-size: 0.8rem;
  }
`;

const Copyright = styled.div`
  text-align: center;
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  color: var(--text-secondary);
  font-size: 0.9rem;
`;

const GithubButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.7rem 1.5rem;
  background-color: #171515;
  color: white;
  border-radius: 4px;
  font-weight: 500;
  transition: all 0.3s ease;
  border: 1px solid #333;
  margin-top: 1rem;

  &:hover {
    background-color: #333;
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  }
`;

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <FooterSection>
      <Container>
        <FooterContent>
          <FooterColumn>
            <FooterLogo>
              RIFT: <span>Last One</span>
            </FooterLogo>
            <FooterDescription>
              An AI-driven observational strategy survival game where players guide characters through an apocalyptic world.
            </FooterDescription>
            <GithubButton href="https://github.com/lastone-games/" target="_blank" rel="noopener noreferrer">
              <FaGithub /> View on GitHub
            </GithubButton>
          </FooterColumn>

          <FooterColumn>
            <FooterTitle>Quick Links</FooterTitle>
            <FooterLinks>
              <FooterLink>
                <StyledLink href="#about">About</StyledLink>
              </FooterLink>
              <FooterLink>
                <StyledLink href="#features">Features</StyledLink>
              </FooterLink>
              <FooterLink>
                <StyledLink href="#monetization">Monetization</StyledLink>
              </FooterLink>
            </FooterLinks>
          </FooterColumn>

          <FooterColumn>
            <FooterTitle>Connect</FooterTitle>
            <SocialLinks>
              <SocialLink href="https://github.com/lastone-games/" target="_blank" rel="noopener noreferrer">
                <FaGithub />
              </SocialLink>
              <SocialLink href="https://x.com/riftlastonegame/" target="_blank" rel="noopener noreferrer">
                <FaTwitter />
              </SocialLink>
            </SocialLinks>
          </FooterColumn>
        </FooterContent>

        <Copyright>
          &copy; {currentYear} RIFT: Last One. All rights reserved.
        </Copyright>
      </Container>
    </FooterSection>
  );
};

export default Footer;
